import type { ModelFamily } from './generatorProfiles';
import type { CompositeInferenceProvider } from './loraRuntime';

// ==================== Z-Image / SDXL family contract (frontend mirror) ====================
// The pinned wire facts for the two LoRA model families, kept in one place so the
// builder, training tab, and docs never drift. Family is sdxl by default and is
// derived from the base model id for HF Tongyi-MAI ids; civitai bases carry an
// explicit family because their id is non-derivable.

export const DEFAULT_FAMILY: ModelFamily = 'sdxl';

/** Hugging Face ids for the Z-Image family. */
export const Z_IMAGE_TURBO_ID = 'Tongyi-MAI/Z-Image-Turbo';
export const Z_IMAGE_BASE_ID = 'Tongyi-MAI/Z-Image';

/** Default base model the z-image family TRAINS on (server-side too), even when inference uses Turbo. */
export const Z_IMAGE_TRAIN_BASE_ID = Z_IMAGE_BASE_ID;

/** Composite-inference provider per family (mirrors the bridge dispatch branches). */
export const INFERENCE_PROVIDER: Record<ModelFamily, CompositeInferenceProvider> = {
  sdxl: 'modal-sdxl-inference-v1',
  'z-image': 'modal-z-image-inference-v1',
};

/** Civitai `baseModels` API filter string per family. */
export const CIVITAI_BASE_MODEL_FILTER: Record<ModelFamily, string> = {
  sdxl: 'SDXL 1.0',
  'z-image': 'ZImageTurbo',
};

/** Normalizes any stored family value (omitted/empty → sdxl). */
export const normalizeFamily = (family: string | null | undefined): ModelFamily =>
  family === 'z-image' ? 'z-image' : 'sdxl';

/**
 * Derives the family from a base model id. HF Tongyi-MAI Z-Image ids map to
 * z-image; everything else (and civitai ids, which are non-derivable) falls back
 * to sdxl — callers with a civitai base must pass the explicit family instead.
 */
export const familyForBaseId = (baseId: string): ModelFamily =>
  /tongyi-mai\/z-image/i.test(baseId) ? 'z-image' : 'sdxl';

/** True when an id is a Z-Image Turbo (distilled) checkpoint — drives cfg=0 / 8-step locking. */
export const isZImageTurboId = (baseId: string): boolean => /z-image-turbo/i.test(baseId) || /zimageturbo/i.test(baseId);

/** Civitai Z-Image checkpoints come from the ZImageTurbo-only catalog filter. */
export const isZImageTurboBase = (
  family: ModelFamily,
  source: string | null | undefined,
  baseId: string,
): boolean => family === 'z-image' && (source === 'civitai' || isZImageTurboId(baseId));

/** Whether a civitai version's reported base model belongs to the given family. */
export const versionMatchesFamily = (versionBaseModel: string, family: ModelFamily): boolean => {
  const upper = (versionBaseModel || '').toUpperCase();
  if (family === 'z-image') return upper.includes('ZIMAGE') || upper.includes('Z-IMAGE') || upper.includes('Z IMAGE');
  return upper.startsWith('SDXL');
};

/** Whether a trained artifact's base_model_family matches the profile family (omitted → sdxl). */
export const artifactMatchesFamily = (artifactFamily: string | null | undefined, family: ModelFamily): boolean =>
  normalizeFamily(artifactFamily) === family;
